// Shared URLSearchParams builder for widgets hitting the scorecard
// service. Pins `requested_organization_id` (NOT organization_id --
// the service silently falls back to the auth-header org otherwise)
// and `scorecard_type=copilot`, then appends the filter dims. Returns
// the URLSearchParams so callers can chain per-endpoint params.

import { useContext, useMemo } from 'react'
import { isEmpty, isNil } from 'lodash'
import { useSelector } from 'react-redux'

import { browserTimezone } from '@/reducers/qa-copilot/qa-copilot.helpers'

import { PickedEntityValuesContext } from './dashboardContexts'

// Resolve the org id the dashboard should be querying. The filter org
// (set when an admin picks another org from the filter bar) takes
// precedence; non-admin sessions fall back to their own org. Returning
// the filter-precedence value matches what every widget was already
// computing inline.
export const useDashboardOrgId = () => {
  const filterOrgId = useSelector((state) => state.qaCopilot.filters?.organizationId)
  const currentUserOrgId = useSelector((state) => state.currentUser?.organizationid)
  return filterOrgId || currentUserOrgId
}

// Build the standard URLSearchParams the dashboard's scorecard-service
// endpoints expect. Pass `agentContext` (an object with a `user_id`)
// for drilldowns that should narrow to a single agent — when present,
// it short-circuits any `filters.agents` entries the user picked from
// the filter bar.
export const buildDashboardParams = (filters, orgId, agentContext = null) => {
  const params = new URLSearchParams()
  params.set('requested_organization_id', orgId)
  params.set('scorecard_type', 'copilot')
  params.set('timezone', browserTimezone())
  if (filters?.startDate) {
    params.set('start_date', new Date(filters.startDate).toISOString())
  }
  if (filters?.endDate) {
    params.set('end_date', new Date(filters.endDate).toISOString())
  }
  if (filters?.scorecards) {
    filters.scorecards.forEach((scorecard) => {
      params.append('scorecard_config_sids', scorecard?.value || scorecard?.sid || scorecard)
    })
  }
  if (filters?.voipCampaigns) {
    filters.voipCampaigns.forEach((campaign) => {
      params.append('voip_campaign_names', campaign?.value || campaign?.name || campaign)
    })
  }
  // Drilldowns override the filter-bar agent list. Without the
  // override the BE would intersect (filter agents) ∩ (drilldown
  // agent) and most drilldowns would silently return empty.
  if (agentContext?.user_id) {
    params.append('agent_ids', String(agentContext.user_id))
  } else if (filters?.agents) {
    filters.agents.forEach((agent) => {
      const id = agent?.value || agent?.user_id || agent?.id || agent
      if (!isNil(id) && id !== '') {
        params.append('agent_ids', String(id))
      }
    })
  }
  if (filters?.tags) {
    filters.tags.forEach((tag) => {
      const id = tag?.value || tag?.id || tag
      if (!isNil(id) && id !== '') {
        params.append('tag_ids', String(id))
      }
    })
  }
  // `dispositions` lives behind an include/exclude mode on the filter
  // bar (`{ selected: [...], includes: true }`). Forward the selected
  // values as `dispositions=...` (one per) AND the toggle as
  // `dispositions_includes=...` so the BE can honor exclude mode --
  // matches what `formatQACopilotFilters` does for legacy callers.
  const dispositionFilter = filters?.dispositions
  if (!isEmpty(dispositionFilter?.selected)) {
    dispositionFilter.selected.forEach((disposition) => {
      const value = disposition?.value || disposition?.name || disposition
      if (!isNil(value) && value !== '') {
        params.append('dispositions', String(value))
      }
    })
    params.set('dispositions_includes', String(dispositionFilter.includes !== false))
  }
  // Channel + status filters mirror what the legacy reporting fetches
  // forward via `sharedScorecardParams`. Without them, switching the
  // filter bar to Messages, picking a playbook, etc., leaves the dashboard
  // widgets stuck on call data.
  if (filters?.playbooks) {
    filters.playbooks.forEach((playbook) => {
      const value = playbook?.value || playbook?.cid || playbook?.id || playbook
      if (!isNil(value) && value !== '') {
        params.append('config_cids', String(value))
      }
    })
  }
  if (filters?.callType) {
    params.set('call_type', filters.callType)
  }
  if (!isNil(filters?.isWin)) {
    params.set('is_win', String(filters.isWin))
  }
  if (filters?.scoredStatus) {
    params.set('scored_status', filters.scoredStatus)
  }
  // Criterion drilldown: narrow scored-call queries to calls where this
  // specific criterion (scid) failed.
  if (filters?.failedCriteriaScid) {
    params.set('failed_criteria_scid', filters.failedCriteriaScid)
  }
  return params
}

const FILTER_KEY_BY_SCOPE = {
  scorecard: 'scorecards',
  agent: 'agents',
  tag: 'tags',
}

// Focus widgets scope to the ONE picked entity, not the cohort. This
// hook returns filters with the scope dim replaced by [picked]. When
// no picker is set, filters pass through unchanged so the widget
// falls back to whatever the user has in the filter bar.
export const useFiltersForPickedScope = (scope) => {
  const filters = useSelector((state) => state.qaCopilot.filters)
  const picked = useContext(PickedEntityValuesContext)?.[scope]
  const filterKey = FILTER_KEY_BY_SCOPE[scope]
  return useMemo(() => {
    if (!filterKey || !picked) {
      return filters
    }
    return { ...filters, [filterKey]: [picked] }
  }, [filters, filterKey, picked])
}
